import React from 'react';
import MainNavbar from '../components/MainNavbar';
import MainFooter from '../components/MainFooter';

const Terms = () => {
  return (
    <div className="font-sans antialiased text-gray-900">
      {/* Navbar */}
      <MainNavbar />
      
      {/* Hero Section */}
      <section className="py-16 bg-gradient-to-r from-yellow-500 via-orange-500 to-red-500 text-white">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h1 className="text-4xl font-bold mb-4">Terms of Service</h1>
          <p className="text-xl mb-4 max-w-2xl mx-auto">
            Last updated: May 1, 2025
          </p>
        </div>
      </section>
      
      {/* Terms Content */}
      <section className="py-16">
        <div className="max-w-3xl mx-auto px-4">
          <div className="space-y-8">
            {/* Introduction */}
            <div className="bg-white rounded-lg shadow-sm p-8">
              <h2 className="text-2xl font-bold mb-4 text-gray-800 border-b border-gray-200 pb-3">1. Agreement to these Terms</h2>
              <div className="space-y-4 text-gray-600">
                <p>
                  Welcome to Dessino.ai ("us", "we", or "our"). Please read these Terms of Service ("Terms") carefully because they govern your use of our websites (collectively, the "Sites"), our mobile and desktop applications ("Apps"), our APIs, and all related services (collectively with the Sites, Apps, and APIs, the "Services").
                </p>
                <p>
                  By using the Services, you agree to be bound by these Terms. If you are accessing and using the Services on behalf of a company (such as your employer) or other legal entity, you represent and warrant that you have the authority to bind that company or other legal entity to these Terms. In that case, "you" and "your" will refer to that company or other legal entity.
                </p>
              </div>
            </div>
            
            {/* Changes to the Terms or Services */}
            <div className="bg-white rounded-lg shadow-sm p-8">
              <h2 className="text-2xl font-bold mb-4 text-gray-800 border-b border-gray-200 pb-3">2. Changes to the Terms or Services</h2>
              <div className="space-y-4 text-gray-600">
                <p>
                  We may modify the Terms at any time, at our sole discretion. If we do so, we'll let you know either by posting the modified Terms on the Site or through other communications. If you continue to use the Services after we have posted modified Terms, you are indicating that you agree to be bound by the modified Terms.
                </p>
                <p>
                  We may also change or discontinue all or any part of the Services, at any time and without notice, at our sole discretion. We will not be liable to you or any third party for any modification, suspension, or discontinuation of the Services.
                </p>
              </div>
            </div>
            
            {/* Service Terms */}
            <div className="bg-white rounded-lg shadow-sm p-8">
              <h2 className="text-2xl font-bold mb-4 text-gray-800 border-b border-gray-200 pb-3">3. Service Terms</h2>
              <div className="space-y-6 text-gray-600">
                <div>
                  <h3 className="text-lg font-semibold mb-2">3.1. Eligibility</h3>
                  <p>
                    You may only use the Services if you are old enough to consent to share your data under applicable law. For example, you must be 13 years or older under most United States' law, or 16 years or older under California or European Union law.
                  </p>
                </div>
                
                <div>
                  <h3 className="text-lg font-semibold mb-2">3.2. Use Restrictions</h3>
                  <p>
                    You will not, and will not permit any third party to: (i) reverse engineer, decompile, disassemble, or otherwise attempt to discover the source code or underlying structure of the Services; (ii) sell, resell, license, sublicense, distribute, rent, or lease the Services; (iii) create derivative works based on the Services; (iv) remove any proprietary notices from the Services; or (v) use the Services in any manner that could damage, disable, overburden, or impair Dessino's systems or networks.
                  </p>
                </div>
                
                <div>
                  <h3 className="text-lg font-semibold mb-2">3.3. Accounts</h3>
                  <p>
                    When you create an account with us, you guarantee that the information you provide is accurate and complete. You are responsible for maintaining the security of your account and password, and you accept responsibility for all activities that occur under your account. You must immediately notify us of any unauthorized use of your account.
                  </p>
                </div>
                
                <div>
                  <h3 className="text-lg font-semibold mb-2">3.4. Feedback</h3>
                  <p>
                    If you provide us with feedback, comments, or suggestions about the Services ("Feedback"), you hereby grant us the right to use such Feedback for any purpose, including to improve and enhance our Services, without any compensation to you. We are not required to hold your Feedback in confidence. You represent and warrant that your Feedback does not contain confidential or proprietary information from you or any third party.
                  </p>
                </div>
                
                <div>
                  <h3 className="text-lg font-semibold mb-2">3.5. Reservation of Rights</h3>
                  <p>
                    Except for the limited rights expressly granted to you under these Terms, we reserve all right, title, and interest in and to the Services and all related intellectual property rights. No license, right, or interest in any trademarks, service marks, or trade names of Dessino is granted to you under these Terms.
                  </p>
                </div>
              </div>
            </div>
            
            {/* License */}
            <div className="bg-white rounded-lg shadow-sm p-8">
              <h2 className="text-2xl font-bold mb-4 text-gray-800 border-b border-gray-200 pb-3">4. License</h2>
              <div className="space-y-6 text-gray-600">
                <div>
                  <h3 className="text-lg font-semibold mb-2">4.1. License to Services</h3>
                  <p>
                    Subject to your compliance with these Terms, we grant you a limited, non-exclusive, non-transferable, non-sublicensable license to access and use the Services for your personal or business purposes.
                  </p>
                </div>
                
                <div>
                  <h3 className="text-lg font-semibold mb-2">4.2. License to User Content</h3>
                  <p>
                    You retain all rights to any content you submit, post, or display on or through the Services ("User Content"). By providing User Content to the Services, you grant us a worldwide, non-exclusive, royalty-free license to use, copy, modify, create derivative works based upon, distribute, and display the User Content for the purpose of operating and providing the Services to you and other users.
                  </p>
                </div>
                
                <div>
                  <h3 className="text-lg font-semibold mb-2">4.3. AI-Generated Content</h3>
                  <p>
                    You retain all rights to any AI-generated content created through the Services. However, you grant us a limited license to use such content to improve our AI systems, in accordance with our AI Terms.
                  </p>
                  <div className="mt-2 p-4 bg-gray-50 rounded-md text-sm">
  <h4 className="font-semibold mb-1">AI Terms Summary:</h4>
  <p className="mb-2">
    Our AI features create content based on your prompts and instructions. Here's how we handle this data:
  </p>
  <ul className="space-y-1 list-disc pl-5">
    <li>You retain full ownership rights to all AI-generated content created through our service</li>
    <li>We may use anonymized data from your interactions to improve our AI models and user experience</li>
    <li>We implement rate limits to ensure fair access to AI features for all users and to meet technical requirements</li>
  </ul>
</div>
                </div>
              </div>
            </div>
            
            {/* Charges and Payment */}
            <div className="bg-white rounded-lg shadow-sm p-8">
              <h2 className="text-2xl font-bold mb-4 text-gray-800 border-b border-gray-200 pb-3">5. Charges and Payment</h2>
              <div className="space-y-6 text-gray-600">
                <div>
                  <h3 className="text-lg font-semibold mb-2">5.1. Pricing</h3>
                  <p>
                    We offer access passes that grant you use of all features for a specific time period (such as 30, 60, 90, 180, or 360 days). Our current pricing plans and features are available on our website. Your rights and access to certain Services will be based on the access pass you purchase.
                  </p>
                </div>
                
                <div>
                  <h3 className="text-lg font-semibold mb-2">5.2. Payment Terms</h3>
                  <p>
                    When you purchase an access pass (each such purchase, a "Purchase"), you expressly authorize us or our third-party payment processor to charge you for such Purchase. You represent and warrant that you have the legal right to use all payment methods that you provide to us.
                  </p>
                  <p className="mt-2">
                    All fees are stated and solely payable in U.S. Dollars (unless otherwise stated during the payment process), non-cancelable, and non-refundable (except as otherwise expressly set forth in these Terms). We may limit your access to the Services if you fail to pay the full amount you owe us.
                  </p>
                  <div className="mt-4 p-4 bg-gray-50 rounded-md">
                    <h4 className="font-semibold mb-2">Important notes about our access passes:</h4>
                    <ul className="space-y-2 list-disc pl-5">
                      <li>Access passes expire automatically after the selected period with no auto-renewals</li>
                      <li>You can purchase a new access pass at any time after expiration</li>
                      <li>During your access period, you have access to all features, subject to reasonable rate limits to prevent abuse or to address technical limitations</li>
                      <li>When your access pass expires, your projects remain stored in your account, but you cannot create new views or make revisions until you purchase a new pass</li>
                    </ul>
                  </div>
                </div>
                
                <div>
                  <h3 className="text-lg font-semibold mb-2">5.3. Taxes</h3>
                  <p>
                    All fees do not include taxes, and you agree to pay all sales, use, value-added, GST, or other taxes applicable to your Purchase. If we are required to collect a tax and did not do so at the time of sale, we reserve the right to charge you later for the applicable tax.
                  </p>
                </div>
              </div>
            </div>
            
            {/* Confidentiality */}
            <div className="bg-white rounded-lg shadow-sm p-8">
              <h2 className="text-2xl font-bold mb-4 text-gray-800 border-b border-gray-200 pb-3">6. Confidentiality</h2>
              <div className="space-y-4 text-gray-600">
                <p>
                  We may disclose proprietary or non-public business, technical, financial, or other information ("Confidential Information") to you. Our Confidential Information expressly includes non-public information regarding features, functionality, and performance of the Services.
                </p>
                <p>
                  You agree to use our Confidential Information only for the purpose of evaluating whether or not to use (or continue to use) the Services, and not to disclose it to third parties without our written consent. You will protect our Confidential Information with the same degree of care that you use to protect your own confidential information of similar nature, but in no case less than reasonable care.
                </p>
                <p>
                  These confidentiality obligations will survive the termination of these Terms for a period of five years.
                </p>
              </div>
            </div>
            
            {/* Warranties and Disclaimers */}
            <div className="bg-white rounded-lg shadow-sm p-8">
              <h2 className="text-2xl font-bold mb-4 text-gray-800 border-b border-gray-200 pb-3">7. Warranties and Disclaimers</h2>
              <div className="space-y-4 text-gray-600">
                <p>
                  In the event of any loss or corruption of your data, we will use commercially reasonable efforts to restore the lost or corrupted data from our latest backup. EXCEPT FOR THE FOREGOING, WE WILL NOT BE RESPONSIBLE FOR ANY LOSS, DESTRUCTION, ALTERATION, UNAUTHORIZED DISCLOSURE, OR CORRUPTION OF ANY DATA.
                </p>
                <p>
                  We make no warranty that the Services will meet your requirements or be available on an uninterrupted, secure, or error-free basis. We make no warranty regarding the quality, accuracy, timeliness, truthfulness, completeness, or reliability of any Services or AI-generated content.
                </p>
                <div className="mt-4 p-4 bg-gray-50 rounded-md">
                  <p className="font-bold text-gray-800">
                    THE SERVICES ARE PROVIDED "AS IS," WITHOUT WARRANTY OF ANY KIND. TO THE MAXIMUM EXTENT PERMITTED BY LAW, WE EXPLICITLY DISCLAIM ALL IMPLIED WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, QUIET ENJOYMENT, AND NON-INFRINGEMENT, AND ANY WARRANTIES ARISING OUT OF COURSE OF DEALING OR USAGE OF TRADE.
                  </p>
                </div>
                <p>
                  We do not warrant or support, and will not have any responsibility or liability for, any third-party applications, plugins, or services that integrate with the Services.
                </p>
              </div>
            </div>
            
            {/* Indemnity */}
            <div className="bg-white rounded-lg shadow-sm p-8">
              <h2 className="text-2xl font-bold mb-4 text-gray-800 border-b border-gray-200 pb-3">8. Indemnity</h2>
              <div className="space-y-4 text-gray-600">
                <p>
                  You agree to indemnify, defend, and hold harmless Dessino.ai and its officers, directors, employees, and agents, from and against any claims, disputes, demands, liabilities, damages, losses, and costs and expenses, including reasonable legal and accounting fees, arising out of or in any way connected with:
                </p>
                <ul className="list-disc pl-6 space-y-1">
                  <li>Your access to or use of the Services</li>
                  <li>Your User Content</li>
                  <li>Your violation of these Terms</li>
                  <li>Your violation of any third-party right, including without limitation any intellectual property right, publicity, confidentiality, property, or privacy right</li>
                  <li>Any claim that your User Content caused damage to a third party</li>
                </ul>
              </div>
            </div>
            
            {/* Limitations of Liability */}
            <div className="bg-white rounded-lg shadow-sm p-8">
              <h2 className="text-2xl font-bold mb-4 text-gray-800 border-b border-gray-200 pb-3">9. Limitations of Liability</h2>
              <div className="space-y-4 text-gray-600">
                <div className="p-4 bg-gray-50 rounded-md">
                  <p className="font-bold text-gray-800">
                    TO THE MAXIMUM EXTENT PERMITTED BY LAW, NEITHER DESSINO.AI NOR ITS SUPPLIERS WILL BE LIABLE FOR ANY INCIDENTAL, SPECIAL, EXEMPLARY, OR CONSEQUENTIAL DAMAGES, INCLUDING LOST PROFITS, LOSS OF DATA OR GOODWILL, SERVICE INTERRUPTION, COMPUTER DAMAGE, SYSTEM FAILURE, OR THE COST OF SUBSTITUTE SERVICES ARISING OUT OF OR IN CONNECTION WITH THESE TERMS OR FROM THE USE OF OR INABILITY TO USE THE SERVICES, WHETHER BASED ON WARRANTY, CONTRACT, TORT (INCLUDING NEGLIGENCE), PRODUCT LIABILITY, OR ANY OTHER LEGAL THEORY, AND WHETHER OR NOT DESSINO.AI HAS BEEN INFORMED OF THE POSSIBILITY OF SUCH DAMAGE.
                  </p>
                </div>
                <p>
                  In no event will our total liability arising out of or in connection with these Terms or from the use of or inability to use the Services exceed the amounts you have paid to us in the preceding twelve months for the Services, or if you have not had any payment obligations, one hundred United States dollars ($100).
                </p>
                <p>
                  The limitations of damages set forth above are fundamental elements of the basis of the bargain between you and us. Some jurisdictions do not allow the exclusion or limitation of liability for consequential or incidental damages, so the above limitation may not apply to you.
                </p>
              </div>
            </div>
            
            {/* Termination */}
            <div className="bg-white rounded-lg shadow-sm p-8">
              <h2 className="text-2xl font-bold mb-4 text-gray-800 border-b border-gray-200 pb-3">10. Termination</h2>
              <div className="space-y-4 text-gray-600">
                <p>
                  We may terminate your access to and use of the Services, at our sole discretion, at any time and without notice to you. If we terminate your access and the termination is not due to your breach of these Terms, we will provide you a pro rata refund of pre-paid unused time on your access pass.
                </p>
                <p>
                  You may cease use of the Services at any time. You may also delete your account at any time by following the instructions in your account settings or by contacting our support team.
                </p>
                <p>
                  Upon termination, the following provisions of these Terms will survive: Service Terms (Section 3), License to User Content (Section 4.2), Charges and Payment (Section 5, to the extent you owe any fees), Confidentiality (Section 6), Warranties and Disclaimers (Section 7), Indemnity (Section 8), Limitations of Liability (Section 9), and Dispute Resolution (Section 11.8).
                </p>
                <p>
                  We may remove or delete your User Content within a reasonable period of time after termination.
                </p>
              </div>
            </div>
            
            {/* Miscellaneous */}
            <div className="bg-white rounded-lg shadow-sm p-8">
              <h2 className="text-2xl font-bold mb-4 text-gray-800 border-b border-gray-200 pb-3">11. Miscellaneous</h2>
              <div className="space-y-6 text-gray-600">
                <div>
                  <h3 className="text-lg font-semibold mb-2">11.1. Privacy Policy</h3>
                  <p>
                    Our Privacy Policy, available on our website, governs how we collect, use and disclose information from the Services. By using our Services, you agree to the terms of our Privacy Policy.
                  </p>
                </div>
                
                <div>
                  <h3 className="text-lg font-semibold mb-2">11.2. Force Majeure</h3>
                  <p>
                    We will not be liable for any delay or failure to perform as required by these Terms as a result of any cause beyond our reasonable control, including acts of God, natural disasters, war, terrorism, riots, embargoes, acts of civil or military authorities, fire, floods, accidents, strikes, or shortages of transportation facilities, fuel, energy, labor, or materials.
                  </p>
                </div>
                
                <div>
                  <h3 className="text-lg font-semibold mb-2">11.3. Notices</h3>
                  <p>
                    Any notices or other communications provided by us under these Terms will be given by posting to the Site or by email to the address you provide to us. For notices made by email, the date of receipt will be deemed the date on which such notice is transmitted.
                  </p>
                </div>
                
                <div>
                  <h3 className="text-lg font-semibold mb-2">11.4. Governing Law</h3>
                  <p>
                    These Terms will be governed by the laws of the Province of Ontario, Canada, without regard to its conflict of laws provisions.
                  </p>
                </div>
                
                <div>
                  <h3 className="text-lg font-semibold mb-2">11.5. Assignment</h3>
                  <p>
                    You may not assign or transfer these Terms, by operation of law or otherwise, without our prior written consent. Any attempt to assign or transfer these Terms without such consent will be void. We may assign or transfer these Terms, at our sole discretion, without restriction.
                  </p>
                </div>
                
                <div>
                  <h3 className="text-lg font-semibold mb-2">11.6. Entire Agreement</h3>
                  <p>
                    These Terms constitute the entire agreement between you and us regarding our Services and supersede and replace any prior agreements we might have had between us regarding the Services.
                  </p>
                </div>
                
                <div>
                  <h3 className="text-lg font-semibold mb-2">11.7. Severability</h3>
                  <p>
                    If any provision of these Terms is held to be invalid or unenforceable, such provision shall be struck and the remaining provisions shall be enforced to the fullest extent under law.
                  </p>
                </div>
                
                <div id="dispute-resolution">
                  <h3 className="text-lg font-semibold mb-2">11.8. Dispute Resolution</h3>
                  <p>
                    In the event of any dispute arising out of or relating to these Terms or your use of the Services, both parties will attempt in good faith to resolve the dispute. If the dispute cannot be resolved by the parties, either party may seek relief through the courts in the Province of Ontario, Canada.
                  </p>
                  <p className="mt-2 text-sm">
                    In some jurisdictions, you may have the option to resolve disputes through arbitration. If you're interested in learning more about dispute resolution options, please contact us.
                  </p>
                </div>
              </div>
            </div>
            
            {/* Contact Us */}
            <div className="bg-white rounded-lg shadow-sm p-8">
              <h2 className="text-2xl font-bold mb-4 text-gray-800 border-b border-gray-200 pb-3">12. Contact Us</h2>
              <div className="space-y-4 text-gray-600">
                <p>
                  If you have any questions about these Terms, please contact us at:
                </p>
                <div className="bg-gray-50 p-4 rounded-md">
                  legal@dessino.ai<br />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
      
      {/* Footer */}
      <MainFooter />
    </div>
  );
};

export default Terms;